// src/components/Login/LoginForm.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAuth,
  signInWithEmailAndPassword,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
} from "firebase/auth";
import { app } from "../../firebase/firebaseConfig";

export default function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const auth = getAuth(app);

  const ensureRecaptcha = () => {
    // Create once; re-use across MFA attempts
    if (!window.recaptchaVerifier) {
   window.recaptchaVerifier = new RecaptchaVerifier(
     auth,                       // 👈 v10: auth FIRST
     "recaptcha-container",
     { size: "invisible" }
   );
  }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/Dashboard");
      return;
    } catch (err) {
      console.error("Login error:", err);

      if (err?.code === "auth/multi-factor-auth-required") {
        // --- Get a resolver (works across SDK versions) ---
        let resolver = null;
        try {
          resolver = getMultiFactorResolver(auth, err);
        } catch (e2) {
          // ignore; try legacy shapes below
        }
        // Some versions stash it here:
        if (!resolver && err?.customData?.mfaResolver) resolver = err.customData.mfaResolver;
        // Very old shapes: build from pending credential + hints (rare)
        // If there's still no resolver, show the raw error to console for clues
        if (!resolver) {
          console.warn("No resolver object. Error customData:", err?.customData);
          setError("Multi-factor step couldn’t start (no resolver). Refresh and try again.");
          return;
        }

        try {
          ensureRecaptcha();

          // Choose the first enrolled factor (or pick by phone number)
          const factorHint = resolver.hints?.[0];
          if (!factorHint) throw new Error("This account has no enrolled phone factor.");

          const phoneInfoOptions = {
            multiFactorHint: factorHint,      // tells Firebase which phone to use
            session: resolver.session,        // ties this verification to the pending sign-in
          };

          const phoneAuthProvider = new PhoneAuthProvider(auth);
          const verificationId = await phoneAuthProvider.verifyPhoneNumber(
            phoneInfoOptions,
            window.recaptchaVerifier
          );

          const smsCode = window.prompt("Enter the 6-digit code sent to your phone:");
          if (!smsCode) throw new Error("Verification code required.");

          const cred = PhoneAuthProvider.credential(verificationId, smsCode);
          const assertion = PhoneMultiFactorGenerator.assertion(cred);

          const userCred = await resolver.resolveSignIn(assertion);
          console.log("✅ Logged in with MFA:", userCred.user?.email);
          navigate("/Dashboard");
          return;
        } catch (mfaErr) {
          console.error("MFA process failed:", mfaErr);
          setError(mfaErr?.message || "Multi-factor verification failed. Try again.");
          return;
        }
      }

      // Non-MFA errors
      let msg = "An unexpected error occurred. Please try again.";
      switch (err.code) {
        case "auth/invalid-email":
          msg = "Please enter a valid email address."; break;
        case "auth/user-disabled":
          msg = "This account has been disabled."; break;
        case "auth/user-not-found":
        case "auth/wrong-password":
          msg = "Email or password is incorrect."; break;
      }
      setError(msg);
    }
  };

  const handleForgotPassword = () => navigate("/forgot-password");

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-semibold text-center text-gray-700 mb-6">Login</h2>

        {error && <p className="text-red-500 text-sm text-center mb-4">{error}</p>}

        <form onSubmit={handleLogin} className="space-y-4">
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
          />
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
          />
          <button type="submit" className="w-full bg-primary text-white p-3 rounded-lg hover:bg-hover transition">
            Log In
          </button>
          <button type="button" onClick={handleForgotPassword} className="w-full text-primary text-sm hover:underline">
            Forgotten Password?
          </button>
        </form>

        <div id="recaptcha-container"></div>
      </div>
    </div>
  );
}
